import React from "react";
import CircleIcon from "@mui/icons-material/Circle";
import { Text } from "@flat-ui";
import { CompanyTypeKind } from "@custom-types/base-types";
import { COMPANY_TYPE_CONFIG } from "@pages/workspace-profile/workspace-constants";
import { Stack, SxProps } from "@mui-wrapper";
import { DropdownWithHelp } from "@components/common/dropdown-with-help";

/** All selectable company types (excludes "Unspecified") */
const SELECTABLE_TYPES: CompanyTypeKind[] = [
  "customer",
  "partner_distributor",
  "internal_rnd",
  "internal_sales",
  "internal_support",
];

interface Props {
  /** Currently selected company type */
  selectedType: CompanyTypeKind | null;

  /** Callback when a company type is selected */
  onTypeSelect: (type: CompanyTypeKind) => void;

  /** The variant of the dropdown */
  variant?: "outlined" | "underlined";

  /** Whether the dropdown should take the full width of its container */
  isFullWidth?: boolean;

  /** Whether to show the label for dropdown or not */
  shouldShowLabel?: boolean;

  /** Whether the dropdown is disabled */
  isDisabled?: boolean;

  /** A fixed width, so that the dropdown does not change width with the selected type */
  width?: string;

  /** Styles of the whole dropdown, e.g. to take away the margin above it */
  sx?: SxProps;
}

/**
 * A reusable dropdown component for selecting company types.
 */
export function WorkspaceTypeDropdown({
  selectedType,
  onTypeSelect,
  variant = "outlined",
  isFullWidth = true,
  shouldShowLabel = true,
  isDisabled,
  width,
  sx,
}: Props): React.JSX.Element {
  return (
    <DropdownWithHelp
      disabled={isDisabled}
      label={shouldShowLabel ? "Workspace Type" : undefined}
      helpTooltip="This is the category of workspace to understand its intended use. This won't be public to the customer and can be changed in the future."
      onChange={(event) => {
        if ((event.target.value as CompanyTypeKind) !== selectedType) {
          onTypeSelect(event.target.value as CompanyTypeKind);
        }
      }}
      fullWidth={isFullWidth}
      width={width}
      sx={sx}
      variant={variant}
      value={selectedType ?? "unspecified"}
      options={SELECTABLE_TYPES.map((type) => ({
        key: type,
        value: type,
        label: (
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <CircleIcon
              sx={{
                color: COMPANY_TYPE_CONFIG[type].color,
                fontSize: 12,
              }}
            />
            <Text variant="bodyL">{COMPANY_TYPE_CONFIG[type].label}</Text>
          </Stack>
        ),
      }))}
    />
  );
}
